// API Configuration
export const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5555';

export const API_ENDPOINTS = {
    // Auth
    LOGIN: '/auth/login',
    AUTH_REFRESH: '/auth/refresh',
    ME: '/me',
    MY_AGENCIES: '/me/agencies',
    MY_PERMISSIONS: '/me/permissions',
    REGISTER: '/users',

    // Users
    USERS: '/users',
    USER_BY_ID: (id: string) => `/users/${id}`,
    USER_BY_USERNAME: (username: string) => `/users/${username}`,
    USER_MEMBERSHIPS: (id: string) => `/users/${id}/memberships`,
    USER_MEMBERSHIP_BY_ID: (userId: string, membershipId: string) => `/users/${userId}/memberships/${membershipId}`,

    // Agencies / RBAC
    AGENCIES: '/agencies',
    AGENCY_BY_ID: (id: string) => `/agencies/${id}`,
    ROLES: '/roles',
    ROLE_BY_ID: (id: string) => `/roles/${id}`,
    PERMISSIONS: '/permissions',
    ACTIVITY_LOGS: '/activity-logs',

    // Products
    PRODUCTS: '/products',
    PRODUCT_BY_ID: (id: string) => `/products/${id}`,
    PRODUCT_SECTIONS: '/products/sections',
    PRODUCT_STATS: '/products/stats',
    PRODUCT_LIKE: (id: string) => `/products/${id}/like`,

    // Categories
    CATEGORIES: '/categories',
    CATEGORY_BY_ID: (id: string) => `/categories/${id}`,
    CATEGORY_STATS: (id: string) => `/categories/${id}/stats`,

    // Orders
    ORDERS: '/orders',
    ORDER_BY_ID: (id: string) => `/orders/${id}`,
    ORDER_STATS: '/orders/stats',
    ORDER_STATUS: (id: string) => `/orders/${id}/status`,
    ORDER_VALIDATE: (id: string) => `/orders/${id}/validate`,

    // Stock Movements
    STOCK_MOVEMENTS: '/stock-movements',
    STOCK_MOVEMENT_BY_ID: (id: string) => `/stock-movements/${id}`,
    STOCK_MOVEMENT_STATS: '/stock-movements/stats',

    // Promotions
    PROMOTIONS: '/promotions',
    PROMOTIONS_ADMIN: '/promotions/admin/all',
    PROMOTION_BY_ID: (id: string) => `/promotions/${id}`,

    // Announcements
    ANNOUNCEMENTS: '/announcements',
    ANNOUNCEMENT_BY_ID: (id: string) => `/announcements/${id}`,
    ANNOUNCEMENTS_ADMIN: '/announcements/admin/all',
    ANNOUNCEMENT_ADMIN_BY_ID: (id: string) => `/announcements/admin/${id}`,

    // Payments
    PAYMENTS: '/payments',
    PAYMENT_BY_ID: (id: string) => `/payments/${id}`,
    PAYMENT_STATS: '/payments/stats',
    PAYMENT_ADD_INSTALLMENT: (id: string) => `/payments/${id}/installment`,
    PAYMENT_SETTLE: (id: string) => `/payments/${id}/settle`,

    // Stats
    STATS_DASHBOARD_SUMMARY: '/stats/dashboard-summary',
    STATS_PROFIT: '/stats/profit',
    STATS_SALES_EVOLUTION: '/stats/sales-evolution',
    STATS_EXPENSES: '/stats/expenses',
    STATS_EXPENSES_EVOLUTION: '/stats/expenses-evolution',

    // Expenses
    EXPENSES: '/expenses',
    EXPENSE_BY_ID: (id: string) => `/expenses/${id}`,

    // Upload
    UPLOAD_IMAGES: '/upload/images',
    UPLOAD_IMAGE: '/upload/image',
    UPLOAD_FILES: '/upload/files',
    UPLOAD_FILE: '/upload/file',
    DELETE_IMAGE: (filename: string) => `/upload/${filename}`,
};

// Helper function to get auth token
export const getAuthToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('auth_token');
};

// Helper function to set auth token
export const setAuthToken = (token: string) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('auth_token', token);
};

// Helper function to remove auth token
export const removeAuthToken = () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('auth_token');
};

export const getRefreshToken = (): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('refresh_token');
};

export const setRefreshToken = (token: string) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('refresh_token', token);
};

export const removeRefreshToken = () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('refresh_token');
};

export const getSelectedAgencyId = (): string | null => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('selected_agency_id');
};

export const setSelectedAgencyId = (agencyId: string) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('selected_agency_id', agencyId);
};

export const clearSelectedAgencyId = () => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem('selected_agency_id');
};

// Helper function to get auth headers
export const getAuthHeaders = () => {
    const token = getAuthToken();
    const agencyId = getSelectedAgencyId();
    return {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(agencyId ? { 'X-Agency-Id': agencyId } : {}),
    };
};
